import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Button,
  Fade,
  Paper,
} from '@mui/material';
import { grey, blue } from '@mui/material/colors';
import { Link } from 'react-router-dom';
import rainImage from '../assets/raindrops_game.png';
import blipsImage from '../assets/blips_game.png'

const cardData = [
  {
    title: 'Rain Drops',
    description:
      'Experience the soothing ambiance of falling raindrops with our interactive game. Enjoy a refreshing experience as you click to play!',
    image: rainImage,
    link: '/game',
    color: blue[500],
    bgColor: blue[50],
  },
  {
    title: 'Blips',
    description:
      'Test your vocab and image association abilities. Dive into our fun and engaging Blips game for an exciting challenge!',
    image: blipsImage,
    link: '/vocab-game',
    color: grey[800],
    bgColor: grey[100],
  },
];

const GamesPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedView, setSelectedView] = useState('grid'); // 'grid' or 'showcase'

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        py: 4, // Reduced vertical padding
        px: 3,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'flex-start' },
            mb: 4, // Reduced margin
          }}
        >
          {/* Left side - Heading and Subheading */}
          <Box sx={{ mb: { xs: 3, md: 0 } }}>
            <Typography
              variant="h4" // Smaller heading
              sx={{
                fontWeight: 600,
                color: '#1a2c42',
                mb: 1, // Reduced margin
              }}
            >
              Game Zone
            </Typography>
            
            <Typography
              variant="subtitle1" // Smaller subheading
              sx={{
                fontWeight: 400,
                color: '#546e7a',
              }}
            >
              Reinforce your learning while having fun!
            </Typography>
          </Box>

          {/* Right side - View Toggle Buttons */}
          <Paper
            elevation={2}
            sx={{
              display: 'flex',
              borderRadius: 50,
              overflow: 'hidden',
              alignSelf: { xs: 'center', md: 'flex-start' },
            }}
          >
            <Button
              variant={selectedView === 'grid' ? 'contained' : 'text'}
              onClick={() => setSelectedView('grid')}
              sx={{
                px: 3, // Reduced padding
                py: 1,
                borderRadius: 50,
                fontWeight: 600,
                backgroundColor: selectedView === 'grid' ? blue[600] : 'transparent',
                color: selectedView === 'grid' ? 'white' : grey[700],
                '&:hover': {
                  backgroundColor: selectedView === 'grid' ? blue[700] : grey[200],
                }
              }}
            >
              Grid View
            </Button>
            <Button
              variant={selectedView === 'showcase' ? 'contained' : 'text'}
              onClick={() => setSelectedView('showcase')}
              sx={{
                px: 3, // Reduced padding
                py: 1,
                borderRadius: 50,
                fontWeight: 600,
                backgroundColor: selectedView === 'showcase' ? blue[600] : 'transparent',
                color: selectedView === 'showcase' ? 'white' : grey[700],
                '&:hover': {
                  backgroundColor: selectedView === 'showcase' ? blue[700] : grey[200],
                }
              }}
            >
              Showcase View
            </Button>
          </Paper>
        </Box>

        {/* Grid View - Ensuring cards don't get cut off */}
        {selectedView === 'grid' && (
          <Grid 
            container 
            spacing={4} // Reduced spacing
            justifyContent="center"
            sx={{ 
              width: '100%', // Ensure container takes full width
              mx: 'auto', // Center the grid
            }}
          >
            {cardData.map((card, index) => (
              <Grid 
                key={index} 
                item 
                xs={12} 
                sm={6} 
                md={6}
                lg={6} // Consistent sizing on large screens
                sx={{ 
                  display: 'flex',
                  width: '100%', // Ensure grid item takes full width
                }}
              >
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%', // Ensure card takes full width of grid item
                    borderRadius: 3,
                    boxShadow: hoveredCard === index ? '0 15px 30px rgba(0,0,0,0.15)' : '0 5px 15px rgba(0,0,0,0.08)',
                    transition: 'all 0.4s ease',
                    transform: hoveredCard === index ? 'translateY(-8px)' : 'translateY(0)',
                    border: hoveredCard === index ? `2px solid ${blue[500]}` : '2px solid transparent',
                  }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <CardActionArea
                    component={Link}
                    to={card.link}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="220" // Adjusted height
                      image={card.image}
                      alt={card.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        p: 3, // Reduced padding
                        backgroundColor: hoveredCard === index ? card.bgColor : 'white',
                        transition: 'background-color 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography 
                        gutterBottom 
                        variant="h5" // Smaller heading
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          color: card.color,
                          mb: 1.5, // Reduced margin
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: grey[700],
                          fontSize: '1rem', // Slightly smaller font
                          lineHeight: 1.6,
                          mb: 2.5, // Reduced margin
                          flexGrow: 1,
                        }}
                      >
                        {card.description}
                      </Typography>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Button 
                          variant="contained"
                          size="medium" // Smaller button
                          sx={{ 
                            backgroundColor: blue[600],
                            borderRadius: 50,
                            px: 3, // Reduced padding
                            py: 1,
                            '&:hover': {
                              backgroundColor: blue[700],
                              filter: 'brightness(110%)',
                            },
                          }}
                        >
                          Play Now
                        </Button>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Showcase View - Ensuring content isn't cut off */}
        {selectedView === 'showcase' && (
          <Box 
            sx={{ 
              mt: 2, // Reduced margin
              width: '100%', // Ensure box takes full width
            }}
          >
            {cardData.map((card, index) => (
              <Fade key={index} in={true} timeout={500 + index * 300}>
                <Paper
                  elevation={3}
                  sx={{
                    mb: 4, // Reduced margin
                    borderRadius: 4,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(0,0,0,0.16)',
                    },
                    transition: 'all 0.4s ease',
                    width: '100%', // Ensure paper takes full width
                  }}
                >
                  <Grid container>
                    <Grid 
                      item 
                      xs={12} 
                      md={6} 
                      sx={{ 
                        display: 'flex',
                        order: { xs: 2, md: index % 2 === 0 ? 1 : 2 }
                      }}
                    >
                      <Box
                        sx={{
                          p: { xs: 3, md: 4 }, // Reduced padding
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          height: '100%',
                          backgroundColor: card.bgColor,
                          width: '100%', // Ensure box takes full width
                        }}
                      >
                        <Typography
                          variant="h4" // Smaller heading
                          sx={{
                            fontWeight: 700,
                            color: card.color,
                            mb: 2, // Reduced margin
                          }}
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: grey[800],
                            fontSize: '1.1rem', // Slightly smaller font
                            lineHeight: 1.7,
                            mb: 3, // Reduced margin
                          }}
                        >
                          {card.description}
                        </Typography>
                        <Box>
                          <Button
                            component={Link}
                            to={card.link}
                            variant="contained"
                            size="medium" // Smaller button
                            sx={{
                              backgroundColor: blue[600],
                              color: 'white',
                              borderRadius: 50,
                              px: 4, // Reduced padding
                              py: 1.25,
                              fontSize: '1rem', // Slightly smaller font
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: blue[700],
                                filter: 'brightness(110%)',
                                transform: 'translateY(-3px)',
                                boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            Play Now
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid 
                      item 
                      xs={12} 
                      md={6} 
                      sx={{ 
                        order: { xs: 1, md: index % 2 === 0 ? 2 : 1 } 
                      }}
                    >
                      <Box
                        sx={{
                          height: { xs: 250, md: 320 }, // Reduced height
                          overflow: 'hidden',
                          width: '100%', // Ensure box takes full width
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={card.image}
                          alt={card.title}
                          sx={{
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default GamesPage;